/**
 * Constantes de permissions et rôles — miroir de App\Acl.php (backend).
 *
 * Usage :
 *   import { P, ROLES } from '@/config/permissions'
 *   $checkPerms([P.COMPTES.BROWSE])
 *   $checkRoles([ROLES.SUPER_ADMIN])
 */

// ──────────────────────────────────────────────
// Rôles
// ──────────────────────────────────────────────
export const ROLES = {
    // Internes
    SUPER_ADMIN: "super admin",
    PROVISIONING_OPERATOR: "provisioning operator",
    ADMIN_HEBERGEMENT: "admin hébergement",
    ADMIN: "admin",
    CHARGE_COMPTE: "chargé de compte",
    TECHNICIEN: "technicien",
    ASTREINTE: "astreinte",
    // Extranet
    EXTRANET_CLIENT: "extranet client",
    // Mixte (interne + extranet)
    MOBILUCE: "mobiluce",
    // Éligibilité (ne pas toucher)
    ELIGIBILITY_PROSOLUCE: "eligibility prosoluce",
    ELIGIBILITY_FULL: "eligibility full",
    ELIGIBILITY_MAX: "eligibility max",
    // Spécialisés
    CHORUS: "chorus",
    UTILISATEUR_EXTERNE: "utilisateur externe"
};

// ──────────────────────────────────────────────
// Permissions — groupées par domaine
// ──────────────────────────────────────────────
export const P = {
    // --- Administration ---
    ADMIN: {
        MANAGE_PERMISSIONS: "manage permissions",
        MANAGE_ROLES: "manage roles"
    },
    NOTIFICATIONS: {
        MANAGE: "manage notifications"
    },

    // --- Utilisateurs ---
    USERS: {
        BROWSE: "browse users",
        CREATE: "create users",
        EDIT: "edit users",
        DELETE: "delete users",
        UNBLOCK_EXTRANET: "unblock extranet comptes"
    },

    // --- Comptes (clients) — inclut contacts & adresses ---
    COMPTES: {
        BROWSE: "browse comptes",
        CREATE: "create comptes",
        EDIT: "edit comptes",
        DELETE: "delete comptes",
        BROWSE_ALL: "browse all comptes",
        MANAGE_EXTRANET: "manage extranet comptes"
    },

    // --- Comptes bancaires ---
    COMPTES_BANCAIRES: {
        BROWSE: "browse comptes bancaires",
        MANAGE: "manage comptes bancaires"
    },

    // --- Multisite ---
    MULTISITE: {
        VIEW: "view multisite",
        MANAGE: "manage multisite"
    },

    // --- Services ---
    SERVICES: {
        BROWSE: "browse services",
        MANAGE: "manage services",
        CONFIGURE: "configure services",
        BROWSE_RENEWABLE: "browse renewable services"
    },

    // --- Biens (+ types de biens) ---
    BIENS: {
        BROWSE: "browse biens",
        MANAGE: "manage biens"
    },

    // --- Notes (CRM) ---
    // BROWSE ouvre l'onglet Notes ; les droits par catégorie décident de ce qui y est affiché.
    NOTES: {
        BROWSE: "browse notes",
        MANAGE: "manage notes",
        BROWSE_GENERAL: "browse notes general",
        BROWSE_TECHNIQUE: "browse notes technique",
        BROWSE_COMMERCIAL: "browse notes commercial",
        BROWSE_ADMINISTRATIF: "browse notes administratif",
        BROWSE_FACTURATION: "browse notes facturation",
        // Écrire n'est pas lire : on consulte plus large qu'on ne saisit. Tout
        // l'historique des notes est rangé en « général », et l'ouvrir en lecture
        // à un rôle ne doit pas lui ouvrir la réécriture.
        MANAGE_GENERAL: "manage notes general",
        MANAGE_TECHNIQUE: "manage notes technique",
        MANAGE_COMMERCIAL: "manage notes commercial",
        MANAGE_ADMINISTRATIF: "manage notes administratif",
        MANAGE_FACTURATION: "manage notes facturation"
    },

    // --- Factures / Invoices ---
    INVOICES: {
        BROWSE: "browse invoices",
        MANAGE: "manage invoices",
        EXPORT: "export invoices"
    },

    // --- Prélèvements (PRLV + RUM) ---
    PRLV: {
        BROWSE: "browse prlv",
        MANAGE: "manage prlv"
    },

    // --- Remises / Dépôts ---
    REMISES: {
        BROWSE: "browse remises",
        MANAGE: "manage remises"
    },

    // --- Commercial ---
    COMMERCIAL: {
        BROWSE: "browse commercial",
        EDIT_CLIENT: "edit commercial client"
    },

    // --- Documents (GED) ---
    DOCUMENTS: {
        BROWSE: "browse documents",
        MANAGE: "manage documents"
    },

    // --- SIP (+ logs) ---
    SIP: {
        BROWSE: "browse sip",
        MANAGE: "manage sip",
        CONFIGURE_DIVERSIONS: "configure diversions"
    },

    // --- Téléphonie monitoring temps réel (ARI + AMI) ---
    TELEPHONY: {
        VIEW_PEERS:    "view telephony peers",
        VIEW_CHANNELS: "view telephony channels"
    },

    // --- Dialplan / Plans d'appel ---
    DIALPLAN: {
        BROWSE: "browse dialplan",
        MANAGE: "manage dialplan"
    },

    // --- Gestion numéros fixes (+ histo, todo, portabilité) ---
    GESTION_NUM_FIXE: {
        BROWSE: "browse gestion num fixe",
        MANAGE: "manage gestion num fixe"
    },

    // --- GIdNumber (associations numéro ↔ poste SIP) ---
    GIDNUMBER: {
        MANAGE: "manage gidnumber"
    },

    // --- Phonebooks / Annuaires / Groupes ---
    PHONEBOOKS: {
        BROWSE: "browse phonebooks",
        MANAGE: "manage phonebooks"
    },

    // --- CDR ---
    CDR: {
        BROWSE: "browse cdr",
        DISMISS_CALLBACKS: "dismiss callbacks"
    },

    // --- Devices / Marques / Modèles ---
    DEVICES: {
        BROWSE: "browse devices",
        MANAGE: "manage devices"
    },

    // --- Switches ---
    SWITCHES: {
        BROWSE: "browse switches"
    },

    // --- Trunks / Prefixes ---
    TRUNKS: {
        MANAGE: "manage trunks"
    },

    // --- Links ---
    LINKS: {
        BROWSE: "browse links",
        MANAGE: "manage links"
    },

    // --- Network Config (Link Types, Providers, Vlans, Zones, IP) ---
    NETWORK_CONFIG: {
        MANAGE: "manage network config",
        // Lecture seule des zones de couverture (overlay de la carte des comptes)
        BROWSE_COVERAGE_ZONES: "browse coverage zones"
    },

    // --- Éligibilité ---
    ELIGIBILITY: {
        BROWSE_LOGS: "browse eligibility logs",
        MANAGE_LOGS: "manage eligibility logs",
        PROSOLUCE: "eligibility prosoluce",
        MAX: "eligibility max"
    },

    // --- VPN (gateways, IP pools, links) ---
    VPN: {
        BROWSE: "browse vpn",
        MANAGE: "manage vpn"
    },

    // --- Tarification / Produits ---
    PRODUCTS: {
        MANAGE: "manage products"
    },

    // --- Failover ---
    FAILOVER: {
        MANAGE: "manage failover"
    },

    // --- Audit ---
    AUDIT: {
        BROWSE_LOGS: "browse audit logs",
        RESTORE_LOGS: "restore audit logs"
    },

    // --- Tokens API ---
    API_TOKENS: {
        MANAGE: "manage api tokens"
    },

    // --- Divers ---
    MISC: {
        SEND_TEST_EMAILS: "send test emails",
        BROWSE_STATS: "browse stats",
        BROWSE_STATS_FOURNISSEURS: "browse stats fournisseurs",
        OLD_EXTRANET: "old extranet"
    },

    // --- HestiaCP (Hébergement) ---
    HESTIA: {
        SERVERS_BROWSE: "hestia-servers-browse",
        SERVERS_MANAGE: "hestia-servers-manage",
        PROFILES_BROWSE: "hestia-profiles-browse",
        PROFILES_MANAGE: "hestia-profiles-manage",
        ACCOUNTS_BROWSE: "hestia-accounts-browse",
        ACCOUNTS_MANAGE: "hestia-accounts-manage",
        MAIL_MANAGE: "hestia-mail-manage",
        DNS_MANAGE: "hestia-dns-manage"
    },

    // --- Extranet (phase 3 : sera consolidé ultérieurement) ---
    EXTRANET: {
        BROWSE_DOCUMENTS: "extranet browse documents",
        BROWSE_SERVICES: "extranet browse services",
        VIEW_SERVICES: "extranet view services",
        BROWSE_INVOICES: "extranet browse invoices",
        BROWSE_SIP: "extranet browse sip",
        EDIT_SIP: "extranet edit sip",
        BROWSE_SIP_LOGS: "extranet browse sip logs",
        BROWSE_CDR: "extranet browse cdr",
        DISMISS_CALLBACKS: "extranet dismiss callbacks",
        BROWSE_PHONEBOOKS: "extranet browse phonebooks",
        CREATE_PHONEBOOKS: "extranet create phonebooks",
        EDIT_PHONEBOOKS: "extranet edit phonebooks",
        DELETE_PHONEBOOKS: "extranet delete phonebooks",
        EXPORT_PHONEBOOKS: "extranet export phonebooks",
        IMPORT_PHONEBOOKS: "extranet import phonebooks",
        SWAP_PHONEBOOKS: "extranet swap phonebooks",
        BROWSE_PHONEGROUPS: "extranet browse phonegroups",
        CREATE_PHONEGROUPS: "extranet create phonegroups",
        EDIT_PHONEGROUPS: "extranet edit phonegroups",
        DELETE_PHONEGROUPS: "extranet delete phonegroups",
        EDIT_SHARE_PHONEGROUPS: "extranet edit share phonegroups",
        PURGE_PHONEGROUPS: "extranet purge phonegroups",
        BROWSE_ALL_COMPTES: "extranet browse all comptes",
        BROWSE_ALL_INVOICES: "extranet browse all invoices",
        VIEW_DASH_COMPTES: "extranet view dash comptes",
        VIEW_DASH_COMPTES_GROUP: "extranet view dash comptes group",
        // Support client : le client suit ses demandes et en dépose.
        BROWSE_TICKETS: "extranet browse tickets",
        MANAGE_TICKETS: "extranet manage tickets",
        VIEW_GESTION_PHONEBOOKS_DASH: "extranet view gestion phonebooks dash",
        VIEW_PDF_INVOICES: "extranet view pdf invoices",
        EDIT_ALERTING_ECAMPAIGN: "extranet edit alerting ecampaign",
        EDIT_PW_ECAMPAIGN: "extranet edit pw ecampaign",
        MANAGE_FAILOVER: "extranet manage failover",
        HESTIA_BROWSE: "extranet hestia browse",
        HESTIA_MANAGE_MAIL: "extranet hestia manage mail"
    },

    // --- Offres commerciales ---
    OFFRES: {
        BROWSE: "browse offres",
        BROWSE_ALL: "browse all offres",
        VIEW: "view offre",
        CREATE: "create offre",
        EDIT: "edit offre",
        DELETE: "delete offre",
        FINALISER: "finaliser offre",
        SIGNER: "signer offre",
        PDF: "generer pdf offre",
        CHECK_ELIGIBILITY: "check eligibility offre",
        VIEW_ELIGIBILITY: "view eligibility offre",
        MANAGE_CATALOGUE: "manage offre catalogue",
        MANAGE_CONDITIONS: "manage offre conditions",
        MANAGE_SECTIONS_PRODUITS: "manage offre sections produits",
        MANAGE_SECTIONS_PRESTATIONS: "manage offre sections prestations",
        MANAGE_PRODUCTION: "manage production taches",
        VIEW_DASHBOARD: "view dashboard offres"
    },

    // --- Taches generiques ---
    // --- Équipes et gestion de projet (kanban) ---
    // Le droit dit ce qu'on sait faire, l'appartenance à l'équipe dit où :
    // le backend ne renvoie jamais que le périmètre des équipes de l'appelant.
    PROJETS: {
        BROWSE: "browse projets",
        MANAGE: "manage projets",
        ADMIN: "admin projets"
    },

    // Processus de service : définir un processus n'est pas l'exécuter.
    PROCESSUS: {
        BROWSE: "browse processus",
        MANAGE: "manage processus",
        ADMIN: "admin processus"
    },

    // Support client : le périmètre de lecture est le guichet — un droit par guichet.
    TICKETS: {
        BROWSE_SUPPORT: "browse tickets support",
        BROWSE_PRODUCTION: "browse tickets production",
        BROWSE_DATACENTER: "browse tickets datacenter",
        BROWSE_COMMERCIAL: "browse tickets commercial",
        MANAGE: "manage tickets",
        CONFIGURE: "configure tickets"
    },

    EQUIPES: {
        BROWSE: "browse equipes",
        // Monter une équipe, dont on devient chef. MANAGE est tout autre chose :
        // administrer les équipes des autres, un droit d'administration générale.
        CREATE: "create equipes",
        MANAGE: "manage equipes"
    },

    AUDIT_PROJETS: {
        BROWSE: "browse audit projets",
        EXPORT: "export audit projets"
    },

    // --- Assistant IA (chatbot interne) ---
    AI: {
        CHAT: "use ai chat"
    },

    // --- Gestion de stock ---
    STOCK: {
        CATALOG_BROWSE:   "stock-catalog-browse",
        CATALOG_MANAGE:   "stock-catalog-manage",
        INVENTORY_BROWSE: "stock-inventory-browse",
        INVENTORY_MANAGE: "stock-inventory-manage",
        ORDERS_BROWSE:    "stock-orders-browse",
        ORDERS_MANAGE:    "stock-orders-manage",
        RECEPTION_BROWSE: "stock-reception-browse",
        RECEPTION_MANAGE: "stock-reception-manage",
        ALERTS_BROWSE:    "stock-alerts-browse",
        ALERTS_MANAGE:    "stock-alerts-manage"
    },

    // --- Provisioning infra ---
    PROVISIONING: {
        ASSETS_BROWSE:    "provisioning.assets.browse",
        ASSETS_MANAGE:    "provisioning.assets.manage",
        ASSETS_PROVISION: "provisioning.assets.provision",
        ASSETS_DRY_RUN:   "provisioning.assets.dry-run",
        ASSETS_PUSH_RAW:  "provisioning.assets.push-raw",
        TEMPLATES_BROWSE: "provisioning.templates.browse",
        TEMPLATES_MANAGE: "provisioning.templates.manage",
        // v2.26.2 Phase 3 — perm DISTINCTE de manage pour toggler
        // is_link_triggerable (2 signatures : dev en config Git + admin ici).
        TEMPLATES_TOGGLE_LINK_TRIGGER: "provisioning.templates.toggle-link-trigger",
        LINK_TRIGGER:     "provisioning.link.trigger",
        SECRETS_BROWSE:   "provisioning.secrets.browse",
        SECRETS_MANAGE:   "provisioning.secrets.manage",
        JOBS_BROWSE:      "provisioning.jobs.browse"
    },

    // --- Mobiluce ---
    MOBILUCE: {
        BROWSE: "mobiluce browse",
        MANAGE_FORWARDS: "mobiluce manage call forwards"
    },

    // --- Déploiement Asterisk (pull) ---
    DEPLOY: {
        BROWSE_JOBS: "browse deploy jobs",
        TRIGGER: "trigger deploy"
    },

    // --- Factures d'achat (Comptabilité) ---
    FACTURES_ACHAT: {
        BROWSE:   "browse factures achat",
        VIEW:     "view factures achat",
        CREATE:   "create factures achat",
        EDIT:     "edit factures achat",
        DELETE:   "delete factures achat",
        VALIDATE: "validate factures achat"
    },

    // --- Paramétrage comptable (plan comptable, journaux, taux de TVA) ---
    PARAMETRAGE_COMPTABLE: {
        BROWSE: "browse parametrage comptable",
        VIEW:   "view parametrage comptable",
        CREATE: "create parametrage comptable",
        EDIT:   "edit parametrage comptable"
    }
};

export const TICKETS_BROWSE_ANY = [P.TICKETS.BROWSE_SUPPORT, P.TICKETS.BROWSE_PRODUCTION, P.TICKETS.BROWSE_DATACENTER, P.TICKETS.BROWSE_COMMERCIAL];

export default P;

// ──────────────────────────────────────────────
// Catégories de permissions (miroir de Acl::CATEGORY_*)
// ──────────────────────────────────────────────
export const PERMISSION_CATEGORIES = [
    { value: "", label: "Tous" },
    { value: "internal", label: "Interne" },
    { value: "extranet", label: "Extranet" },
    { value: "infra", label: "Infra" }
];
